/*
 * assets/local-site-bar.js : the Local Site Bar behaviour (owner decision 2026-09-05 ; the full rationale sits in
 * the header comment of sections/local-site-bar.liquid).
 *
 * Runs once for the bar that section renders, in this order of decisions :
 *   1. Theme editor : show at once with the sample country so the bar can be styled, no request made.
 *   2. Dismissed earlier in this browser : do nothing. The customer answered the question, so it is remembered in
 *      localStorage rather than for one tab session (the Status Alert Bar's choice) : an outage notice is worth
 *      repeating, an invitation to switch website is not.
 *   3. Suggestion already fetched in this tab : reuse it (sessionStorage), so Shopify is asked once a visit, not
 *      once a page.
 *   4. Otherwise ask Shopify's browsing_context_suggestions.json, excluding the country the visitor is already on,
 *      exactly as shopify.dev documents in "Detect and set a visitor's optimal localization", and cache the answer,
 *      including "no suggestion", so the own-site case costs one request a visit too. A dropped connection or a
 *      refused response (a 429 or 503, a bot-protection interstitial) is NOT an answer and is never cached : the
 *      bar stays hidden for that page and the next page asks again (PR #112 round 2).
 *
 * HOW THE SWITCH HAPPENS : the bar's line is a submit button inside Shopify's own localization form ; this script
 * only fills the form's country_code with the suggested country and reveals the bar. Shopify performs the switch
 * (the redirect to the other domain through its /localization hand-off, and the return to the same page via the
 * form's return_to), exactly as it does for the footer country selector. No cross-domain URL is built here.
 *
 * WHETHER A COUNTRY HAS A SITE OF ITS OWN is read from the <link rel="alternate" hreflang="xx-CC"> tags Shopify emits
 * in content_for_header, one per market web presence. The primary market has no country-coded link : Shopify emits
 * it as hreflang="en" and "x-default", so x-default counts ONLY when the suggested country is the primary market's
 * own (config.defaultCountry, United States today). Any other country without a country-coded link has no site of
 * its own and nothing shows : Shopify tags each alternate with its market's primary country, so a country served by
 * a multi-country market never gets one, and offering it a switch under its own name would be a wrong offer
 * (PR #112 round 1). A site that resolves to the domain the visitor is already on is never offered.
 *
 * THE FLAG (owner decision 2026-09-09) : the detected country's flag takes the arrow's place at the end of the line,
 * from Shopify's static flag set (config.flagUrl, one image per ISO code). A flag that fails to load hands the line
 * back to the arrow, so it never ends with nothing. The editor's sample country shows config.sampleCode's flag.
 *
 * Storage access is wrapped because some browsers throw on it (private modes) ; a throw means "not stored".
 * The theme editor re-renders a section after every setting change and does not re-run this file, so the
 * shopify:section:load event re-initialises the fresh markup (that event fires only in the editor).
 *
 * EACH BAR IS INITIALISED ONCE : the section renders its own script tag, so a second placement in the header group
 * evaluates this file twice, and every run walks every bar on the page. Without a per-bar flag that doubled the
 * dismiss and flag listeners and asked Shopify twice (the suggestion is cached only when the answer arrives), and
 * in the editor the two shopify:section:load handlers initialised each re-rendered bar twice. The flag lives on the
 * bar itself so re-rendered markup starts clean and nothing leaks into the page (section audit, 2026-09-11).
 */
(function () {
  var DISMISSED_KEY = 'local-site-bar-dismissed';
  var SUGGESTION_KEY = 'local-site-bar-suggestion';

  function read(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function write(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (e) {
      // Not stored : the bar simply asks again next page.
    }
  }

  function hasLocalSite(code, isDefaultCountry) {
    var alternate = document.querySelector('link[rel="alternate"][hreflang$="-' + code + '"]');
    if (!alternate && isDefaultCountry) {
      alternate = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    }
    if (!alternate) return false;
    return new URL(alternate.href).host !== window.location.host;
  }

  function pick(payload) {
    var first = payload && payload.suggestions && payload.suggestions[0];
    var country = first && first.parts && first.parts.country;
    return country ? { code: country.handle, name: country.name } : null;
  }

  function init(bar) {
    if (bar.dataset.localSiteBarReady) return;
    bar.dataset.localSiteBarReady = 'true';
    var config = JSON.parse(bar.querySelector('[data-local-site-config]').textContent);
    var message = bar.querySelector('[data-local-site-message]');
    var country = bar.querySelector('[data-local-site-country]');
    var flag = bar.querySelector('[data-local-site-flag]');
    var arrow = bar.querySelector('[data-local-site-arrow]');

    if (flag) {
      flag.addEventListener('error', function () {
        flag.hidden = true;
        if (arrow) arrow.hidden = false;
      });
    }

    function show(name, code, flagCode) {
      message.textContent = config.text.replace('[country]', name);
      country.value = code;
      if (flag && flagCode) {
        flag.src = config.flagUrl.replace('{code}', flagCode.toLowerCase());
        flag.hidden = false;
        if (arrow) arrow.hidden = true;
      }
      bar.hidden = false;
    }

    function apply(suggestion) {
      if (!suggestion) return;
      if (!hasLocalSite(suggestion.code, suggestion.code === config.defaultCountry)) return;
      show(config.names[suggestion.code] || suggestion.name, suggestion.code, suggestion.code);
    }

    bar.querySelector('[data-local-site-dismiss]').addEventListener('click', function () {
      bar.hidden = true;
      // The theme editor preview shares the storefront's storage : a click while styling must not silence the
      // live bar in that browser (PR #112 round 1).
      if (!config.designMode) write('localStorage', DISMISSED_KEY, 'true');
    });

    if (config.designMode) {
      show(config.sampleCountry, '', config.sampleCode);
      return;
    }
    if (read('localStorage', DISMISSED_KEY)) return;

    var cached = read('sessionStorage', SUGGESTION_KEY);
    if (cached !== null) {
      apply(JSON.parse(cached));
      return;
    }

    var query = 'browsing_context_suggestions.json?country[enabled]=true';
    if (window.Shopify.country) query += '&country[exclude]=' + encodeURIComponent(window.Shopify.country);
    fetch(window.Shopify.routes.root + query, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('SUGGESTION REQUEST REFUSED : HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var suggestion = pick(payload);
        write('sessionStorage', SUGGESTION_KEY, JSON.stringify(suggestion));
        apply(suggestion);
      })
      .catch(function () {
        // Detection failed : the bar stays hidden and the footer country selector remains.
      });
  }

  document.querySelectorAll('[data-local-site-bar]').forEach(init);
  document.addEventListener('shopify:section:load', function (event) {
    var bar = event.target.querySelector('[data-local-site-bar]');
    if (bar) init(bar);
  });
})();
